import {
    Banner,
    Bleed,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    Collapsible,
    Divider,
    Icon,
    InlineError,
    InlineStack,
    Layout,
    RadioButton,
    Select,
    Text,
    TextField,
    Tooltip,
} from '@shopify/polaris';
import React, { useState, ReactElement, useContext } from 'react';
import CustomCard from '../../../../Utilities/CustomCard';
import { DeleteMinor, InfoMinor, PlusMinor } from '@shopify/polaris-icons';
import { useMediaQuery } from '../../../../Utilities/useMediaQuery';
import { DI, DIProps } from '../../../../../Core';
import useContextLayer from '../../Context/helper';
import { businessPriceValidator, getMarketplaceId, renderFormattedPrice } from '../../utils/utils';
import { UserContext } from '../../../../../Actions/NecessaryFun';
import { useTranslation } from 'react-i18next';
import useContainerState from '../../Context/ContainerState';
import { formatPrice } from '../Price/pricehelper';


interface Props extends DIProps {
    setShowBusinessPrice: (e: boolean) => void;
    showBusinessPrice: boolean;
}
interface QuantityDiscount {
    "quantity": string,
    "price": string
}
interface BusinessPriceI {
    selection_type: "default" | "custom" | "off",
    quantity_type: "fixed" | "percent",
    quantity_discount: QuantityDiscount[],
    attribute: string,
    value: string,
}

function BusinessPriceN(props: Props): ReactElement {
    const { t } = useTranslation();
    const [duplicateQuantityError, setDuplicateQuantityError] = useState<boolean>(false);
    const [hierarchyError, setHierarchyError] = useState<boolean>(false);
    const context: any = useContext(UserContext);
    const { setBusinessPriceErrors, businessPriceErrors } = context;
    const { state, dispatch } = useContextLayer();
    const { containerState } = useContainerState()
    const { data, selection_type: selectionType, raw_data } = state;
    const { rawBusinessPriceSettings, rawShowBusinessPrice, rawBusinessPriceEnabled, from } = raw_data;
    const { business_price, business_price_enabled: businessPriceEnabled } = data ?? { business_price: {}, business_price_enabled: false };
    const businessPrice = JSON.parse(JSON.stringify(business_price ?? {}));
    const [open, setOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width: 690px)');
    const options = [
        { label: 'Decrease price by', value: 'decrease_by' },
        { label: 'Increase price by', value: 'increase_by' },
    ];

    const effectiveBusinessPrice = selectionType?.business_price == "default" ? JSON.parse(JSON.stringify(rawBusinessPriceSettings)) ?? {} : JSON.parse(JSON.stringify(businessPrice));
    const effectiveBusinessPriceEnabled = selectionType?.business_price == "default" ? rawBusinessPriceEnabled ?? false : businessPriceEnabled;
    const effectiveShowBusinessPrice = selectionType?.business_price == "default" ? rawShowBusinessPrice ?? false : props?.showBusinessPrice;

    const roundOffOptions: any = [
        {
            title: t('Nearest Higher'),
            options: [
                { label: t("Whole number"), value: "higherWholeNumber" },
                { label: t("End with 9"), value: "higherEndWith9" },
                { label: t("End with 10"), value: "higherEndWith10" },
                { label: t("End with 0.49"), value: "higherEndWith0.49" },
                { label: t("End with 0.99"), value: "higherEndWith0.99" },
            ]
        },
        {
            title: 'Nearest Lower',
            options: [
                { label: t("Whole number"), value: "lowerWholeNumber" },
                { label: t("End with 9"), value: "lowerEndWith9" },
                { label: t("End with 10"), value: "lowerEndWith10" },
                { label: t("End with 0.49"), value: "lowerEndWith0.49" },
                { label: t("End with 0.99"), value: "lowerEndWith0.99" },
            ]
        },
        {
            title: t('No Rounding Off'),
            options: [
                { label: t("No round off"), value: "" },
            ]
        },
    ];

    const handleRow = (type: string, index: number, operation: string) => {
        const tempQuantityDiscount = JSON.parse(JSON.stringify(effectiveBusinessPrice?.[type]));
        let tempBusinessErrors = JSON.parse(JSON.stringify(businessPriceErrors));
        if (operation == "delete") {
            tempQuantityDiscount?.splice(index, 1);
            tempBusinessErrors[type].splice(index, 1);
        }
        else {
            tempQuantityDiscount.push({
                quantity: "",
                price: ""
            });
            tempBusinessErrors[type] = [...tempBusinessErrors[type], {
                quantity: false,
                price: false,
                touched: {
                    quantity: true,
                    price: true
                }
            }];
        }
        setBusinessPriceErrors(JSON.parse(JSON.stringify(tempBusinessErrors)))
        dispatch({
            type: 'business_price',
            payload: {
                business_price: { ...effectiveBusinessPrice, [type]: tempQuantityDiscount }
            }
        });
    }

    const getFinalPriceRowWise = (discountPrice: any) => {
        let price = getFinalPriceWithRoundOff(state, effectiveBusinessPrice, props) ?? 0;
        if (effectiveBusinessPrice?.quantity_type && price && price > 0) {
            if (effectiveBusinessPrice?.quantity_type == "fixed") {
                price = +price - +discountPrice
            }
            else if (effectiveBusinessPrice?.quantity_type == "percent") {
                price = +price - (+price * +discountPrice / 100)
            }
        }
        return price
    }

    const getRowPriceBreakUp = (index: number) => {
        let price: any = "";
        let bp = getFinalPriceWithRoundOff(state, effectiveBusinessPrice, props);

        let content = effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.price != "" && effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.quantity != "" ? <React.Fragment>
            {bp && <InlineStack blockAlign='center' align='space-between'>
                <Text as='p' tone='subdued'>{t("Business Price:")}</Text>
                <Text as='p'>{getCurrency(props)}{' '}{bp}</Text>
            </InlineStack>}
            <InlineStack blockAlign='center' align='space-between'>
                <Text as='p' tone='subdued'>{t("Discount:")}</Text>
                {effectiveBusinessPrice?.quantity_type == "percent" ? <Text as='p'>{`-${effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.price}%`}</Text> : <Text as='p'>{`${getCurrency(props)} -${effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.price}`}</Text>}
            </InlineStack>
        </React.Fragment> : null

        return content
    }

    const quantityDiscountMarkUp = effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.map((row: QuantityDiscount, index: number) => {

        return <BlockStack gap={'100'} key={index}>
            <Text as='p' fontWeight='medium' variant='bodyMd' tone='subdued'>{t("Quantity Tier")}{" "}{index + 1}</Text>
            <BlockStack gap={"200"}>
                <div className='custom-business-price-quantiy-discount'>
                <InlineStack
                    gap={'300'}
                    blockAlign='end'
                    wrap={true}>
                    <TextField
                        label={t("Minimum order quantity")}
                        autoComplete="off"
                        value={row?.quantity}
                        placeholder={t("Enter value")}
                        onChange={(e) => {
                            e = e.toString()
                                .replace(/[\sa-zA-Z-!$%^&*()@_+|~=`\\#{}\[\]:";'<>?,.\/]/gi, "");
                            handleChange(effectiveBusinessPrice?.quantity_type, e, "quantity", index);
                            let tempErrors = JSON.parse(JSON.stringify(businessPriceErrors));
                            tempErrors[effectiveBusinessPrice?.quantity_type] = validatePriceEntries(effectiveBusinessPrice, state, getFinalPrice, "product_edit", effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type], [...businessPriceErrors[effectiveBusinessPrice?.quantity_type]], "update", t, !(containerState['product_type']['variantEdit'] || !containerState.initialProductData['hasVariants']));
                            setBusinessPriceErrors(JSON.parse(JSON.stringify(tempErrors)));
                        }}
                        error={selectionType?.business_price == "custom" ? businessPriceErrors?.[effectiveBusinessPrice?.quantity_type]?.[index]?.['quantity'] : false}
                    />
                    <TextField
                        disabled={effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.quantity == ""}
                        label={effectiveBusinessPrice?.quantity_type == "fixed" ? t("Your discount price/unit") : t("Your discount percentage/unit")}
                        prefix={effectiveBusinessPrice?.quantity_type == "percent" ? "%" : null}
                        autoComplete="off"
                        value={row?.price}
                        placeholder={effectiveBusinessPrice?.quantity_type == "fixed" ? t("Enter price") : t("Enter percentage")}
                        onChange={(e) => {
                            e = e.trim().replace(/[a-zA-Z-!$%^&*()@_+|~=`\\#{}\[\]:";'<>?,\/]/gi, '');
                            e = e?.[0] === '.' ? '0' + e : e;
                            e = (e.split(".").length - 1 > 1 ? e.slice(0, -1) : e).replace(/^(\d+.?\d{0,2})\d*$/, "$1");
                            handleChange(effectiveBusinessPrice?.quantity_type, e, "price", index);
                            let tempErrors = JSON.parse(JSON.stringify(businessPriceErrors));
                            tempErrors[effectiveBusinessPrice?.quantity_type] = validatePriceEntries(effectiveBusinessPrice, state, getFinalPrice, "product_edit", effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type], [...businessPriceErrors[effectiveBusinessPrice?.quantity_type]], "update", t, !(containerState['product_type']['variantEdit'] || !containerState.initialProductData['hasVariants']));
                            setBusinessPriceErrors(JSON.parse(JSON.stringify(tempErrors)))
                        }}
                        error={selectionType?.business_price == "custom" ? businessPriceErrors?.[effectiveBusinessPrice?.quantity_type]?.[index]?.['price'] : false}
                    />
                    {(containerState['product_type']['variantEdit'] || !containerState.initialProductData['hasVariants']) &&
                        <div className='Custom-Stack-Item--fill'>
                            <BlockStack gap={'100'}>
                                <Text
                                    as="p"
                                    variant="bodyMd">
                                    {t("Final price/unit")}
                                </Text>
                                <InlineStack
                                    gap={'400'} wrap={false}>
                                    <CustomCard
                                        background="rgba(0, 0, 0, 0.06)"
                                        customClass="business__finalPrice Custom-Stack-Item--fill">
                                        <InlineStack
                                            gap={
                                                '100'
                                            }
                                            wrap={
                                                false
                                            }
                                            align='space-between'
                                        >
                                            <Text
                                                as="p"
                                                fontWeight="medium">
                                                {`${getCurrency(props)} ${(effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.quantity == "" && effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.price == "") ? 0 : roundNumber(getFinalPriceRowWise(effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.price), 2)}`}
                                            </Text>
                                            {effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.quantity !== "" && +effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.quantity > 0 && effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.price !== "" && +effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.[index]?.price > 0 && <Tooltip content={getRowPriceBreakUp(index)}>
                                                <Icon
                                                    source={
                                                        InfoMinor
                                                    }
                                                />
                                            </Tooltip>}
                                        </InlineStack>
                                    </CustomCard>
                                    {/* <Button
                                        icon={
                                            DeleteMinor
                                        }
                                        variant="tertiary"
                                        onClick={() => handleRow(effectiveBusinessPrice?.quantity_type, index, "delete")}
                                        disabled={effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.length == 1}
                                    /> */}
                                </InlineStack>
                            </BlockStack>
                        </div>}
                    <div style={{ marginBottom: "2px" }}>
                        <Button
                            icon={
                                DeleteMinor
                            }
                            variant="tertiary"
                            onClick={() => handleRow(effectiveBusinessPrice?.quantity_type, index, "delete")}
                            disabled={effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.length == 1}
                        />
                    </div>
                </InlineStack>
                </div>
                {businessPriceErrors?.[effectiveBusinessPrice?.quantity_type]?.[index]?.hasError && (
                    <BlockStack gap={"100"}>
                        {businessPriceErrors?.[effectiveBusinessPrice?.quantity_type]?.[index]?.errors.map((errMsg: string, idx: number) => (
                            <InlineError message={errMsg} fieldID={`${errMsg}_${idx}`}></InlineError>
                        ))}
                    </BlockStack>
                )}
            </BlockStack>
        </BlockStack>
    })

    const handleChange = (key: string, value: any, subKey?: string, ind?: any) => {
        if (key == "business_price_enabled") {
            dispatch({
                type: 'business_price_enabled',
                payload: {
                    business_price_enabled: value
                }
            })
            if (!value) {
                setBusinessPriceErrors({
                    selectionType: false,
                    attribute: false,
                    value: false,
                    quantity_type: false,
                    fixed: [{ quantity: false, price: false }],
                    percent: [{ quantity: false, price: false }],
                })
            }
        }
        // else if (key == "selection_type") {
        //     dispatch({
        //         type: 'selection_type',
        //         payload: {
        //             business_price: value
        //         }
        //     });
        // }
        else {
            let tempBusinessPrice = effectiveBusinessPrice;
            if (subKey) {
                tempBusinessPrice[key][ind][subKey] = value;
            }
            else tempBusinessPrice[key] = value;
            dispatch({
                type: 'business_price',
                payload: {
                    business_price: tempBusinessPrice
                }
            });
        }
    }

    const shouldAddRowDisabled = () => {
        let disabled = false;
        effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.map((discountRow: any) => {
            if (discountRow?.quantity == "" || discountRow?.price == "") {
                disabled = true
                return
            }
        })
        let maxLength = effectiveBusinessPrice?.[effectiveBusinessPrice?.quantity_type]?.length === 5
        return disabled || maxLength
    }

    function roundOffText(round_off_option: any) {
        switch (round_off_option) {
            case 'higherWholeNumber': return t('Nearest Higher Whole Number');
            case 'higherEndWith9': return t('Nearest Higher Ending with 9');
            case 'higherEndWith0.99': return t('Nearest Higher Ending with 0.99');
            case 'higherEndWith0.49': return t('Nearest Higher Ending with 0.49');
            case 'higherEndWith10': return t('Nearest Higher Ending with 10');
            case 'lowerWholeNumber': return t('Nearest Lower Whole Number');
            case 'lowerEndWith9': return t('Nearest Lower Ending with 9');
            case 'lowerEndWith0.99': return t('Nearest Lower Ending with 0.99');
            case 'lowerEndWith0.49': return t('Nearest Lower Ending with 0.49');
            case 'lowerEndWith10': return t('Nearest Lower Ending with 10');
            default: return t('No round off');
        }
    }

    const getBusinessPriceBreakUp = () => {
        // let price: any = "";
        // let deafultPriceWithRules = state?.selection_type?.price == "custom" ? state?.data?.price : renderFormattedPrice(state?.['raw_data']?.['price'], props)
        // price = +(state?.selection_type?.price == "custom" ? deafultPriceWithRules : removeCurrencySign(deafultPriceWithRules?.props?.value));
        const { data, selection_type: selectionType, raw_data } = state;
        const { rawPriceSettings } = raw_data;
        const { currency_settings, from, shopify_price, standard_price } = rawPriceSettings;
        const { price } = data;
        const effectivePriceSettings = selectionType?.price == "default" ? standard_price : price;


        let myPrice: any = "";
        if (effectivePriceSettings?.attribute === "custom") {
            myPrice = effectivePriceSettings?.value
        }
        else if (effectivePriceSettings?.attribute == "default") {
            myPrice = shopify_price;
        }
        else {
            let formattedPrice: any = formatPrice({
                props: props,
                shopify_price: shopify_price ?? 0,
                settings_enabled: effectivePriceSettings && Object?.keys(effectivePriceSettings)?.length > 0,
                standard_price: effectivePriceSettings ?? [],
                currency_settings: currency_settings
            });
            myPrice = +(formattedPrice?.currency_settings?.calculated_value ?? 0);
        }


        let content = <React.Fragment>
            {/* <Text as='p' fontWeight='semibold'>Price Breakup:</Text> */}
            {myPrice && <InlineStack blockAlign='center' align='space-between'>
                <Text as='p' tone='subdued'>{t("Product's Calculated Price:")}</Text>
                <Text as='p'>{getCurrency(props)}{' '}{myPrice}</Text>
            </InlineStack>}
            {effectiveBusinessPrice?.attribute && effectiveBusinessPrice?.value && <InlineStack blockAlign='center' align='space-between'>
                <Text as='p' tone='subdued'>{t("Discount:")}</Text>
                {effectiveBusinessPrice?.attribute == "percentage_decrease" ? <Text as='p'>{`-${effectiveBusinessPrice?.value}%`}</Text> : <Text as='p'>{`${getCurrency(props)} -${effectiveBusinessPrice?.value}`}</Text>}
            </InlineStack>}
            {effectiveBusinessPrice?.price_roundoff_value && <InlineStack blockAlign='center' align='space-between'>
                <Text as='p' tone='subdued'>{t("Price Round Off:")}</Text>
                <Text as='p'>{roundOffText(effectiveBusinessPrice?.price_roundoff_value)}</Text>
            </InlineStack>}
        </React.Fragment>
        return content
    }

    let tempErrors: any = JSON.parse(JSON.stringify(businessPriceErrors))

    // function evaluateBusinessErrors(obj: any, quantityType: string) {
    //     // Helper function to check values recursively
    //     function checkRecursive(value: any): boolean {
    //         if (typeof value === 'boolean') {
    //             return value;
    //         }
    //         if (Array.isArray(value)) {
    //             return value.some(item => checkRecursive(item));
    //         }
    //         if (typeof value === 'object' && value !== null) {
    //             return Object.values(value).some(innerValue => checkRecursive(innerValue));
    //         }
    //         return false;
    //     }

    //     // Check the main object fields
    //     const mainFields = { ...obj };
    //     delete mainFields.fixed;
    //     delete mainFields.percent;

    //     if (checkRecursive(mainFields)) {
    //         return true;
    //     }

    //     // Check the array based on quantityType
    //     if (quantityType === "fixed" && obj.fixed) {
    //         return checkRecursive(obj.fixed);
    //     } else if (quantityType === "percent" && obj.percent) {
    //         return checkRecursive(obj.percent);
    //     }

    //     return false;
    // }

    function evaluateBusinessErrors(obj: any, quantityType: string): { result: boolean; keys: string[] } {
        // Helper function to check values recursively and collect keys
        function checkRecursive(value: any, parentKey: string): string[] {
            let keys: string[] = [];

            if (typeof value === 'boolean' && value) {
                keys.push(parentKey);
            }

            if (Array.isArray(value)) {
                value.forEach((item, index) => {
                    keys = keys.concat(checkRecursive(item, `${parentKey}[${index}]`));
                });
            }

            if (typeof value === 'object' && value !== null) {
                Object.entries(value).forEach(([key, val]) => {
                    // Skip specific keys: touched.quantity and touched.price
                    if (
                        (parentKey.includes("fixed") || parentKey.includes("percent")) &&
                        key === "touched"
                    ) {
                        return;
                    }

                    keys = keys.concat(checkRecursive(val, `${parentKey}.${key}`));
                });
            }

            return keys;
        }

        // Check the main object fields
        const mainFields = { ...obj };
        delete mainFields.fixed;
        delete mainFields.percent;

        const mainKeys = checkRecursive(mainFields, '');

        // Check the array based on quantityType
        let typeKeys: string[] = [];
        if (quantityType === "fixed" && obj.fixed) {
            typeKeys = checkRecursive(obj.fixed, 'fixed');
        } else if (quantityType === "percent" && obj.percent) {
            typeKeys = checkRecursive(obj.percent, 'percent');
        }

        // Combine keys from main fields and the selected array
        const allKeys = mainKeys.concat(typeKeys);

        return { result: allKeys.length > 0, keys: allKeys };
    }


    // let { businessErrData, businessErr } = businessPriceValidator(state, tempErrors, props.showBusinessPrice, props, t, containerState);
    let { result, keys } = evaluateBusinessErrors(tempErrors, effectiveBusinessPrice?.quantity_type)

    return (
        <Card background="bg-surface" roundedAbove="sm">
            <Layout>
                <Layout.AnnotatedSection
                    title={t("Business Price")}
                    description={<InlineStack><Text as={'p'}>{containerState.initialProductData.type == "simple" && !containerState.product_type.variantEdit ? t("Establish a discounted price for bulk buyers, such as businesses, on Amazon. Apply business price rules to this product by choosing the existing business price settings or adjusting the value.") : containerState.product_type.variantEdit ? t("Establish a discounted price for bulk buyers, such as businesses, on Amazon. Apply business price rules to this variant by choosing the existing business price settings or adjusting the value.") : t("Establish a discounted price for bulk buyers, such as businesses, on Amazon. Apply business price rules to this product's variants by choosing the existing business price settings or adjusting the value.")}</Text><br /><Text as={'p'} children={t("The price structure flows from the Standard Price to the Business Price, followed by Quantity Tiers 1 through 5, and concludes with the Minimum Price.")}></Text>{containerState.initialProductData['hasVariants'] && <><br /><Text as='p'>{t("Business price rules defined for the parent product are automatically applied to its variants, as Amazon does not accept parent-level pricing. Edit individual variants for further customization if needed.")}</Text></>}</InlineStack>}>
                    {/* <div className={businessErr ? "Custom-Polaris-Box custom-error-description" : 'Custom-Polaris-Box'}> */}
                    <div className={'Custom-Polaris-Box'}>
                        <Box
                            padding="400"
                            background="bg-surface-secondary"
                            borderRadius="300"
                            borderWidth="025"
                            borderColor="border">
                            {/* <Box padding='400' background="bg-surface-secondary" borderRadius='300' borderWidth='025' borderColor='border'> */}
                            <BlockStack gap="300">
                                <InlineStack gap="300">
                                    <RadioButton
                                        label={t("Set Existing Business Price Rules")}
                                        id="businesspricedefault"
                                        name="businessprice"
                                        checked={(state?.['selection_type']?.['business_price'] ?? 'default') === 'default'}
                                        onChange={() => {
                                            dispatch({
                                                type: 'selection_type',
                                                payload: {
                                                    business_price: 'default'
                                                }
                                            });
                                        }}
                                        helpText={containerState.initialProductData.type == "simple" && !containerState.product_type.variantEdit ? t("Choosing this will retain the previously applied business price settings. Business Price settings follow a hierarchy: Edited Product > Template > Global Settings.") : containerState.product_type.variantEdit ? t("Choosing this will retain the previously applied business price settings. Business Price settings follow a hierarchy: Edited Product > Variant's Parent > Template > Global Settings") : t("Choosing this will retain the previously applied business price settings. Business Price settings follow a hierarchy: Edited Product > Template > Global Settings.")}
                                    />
                                    <RadioButton
                                        label={t("Set Custom Business Price on Amazon")}
                                        id="businesspricecustom"
                                        name="businesscustom"
                                        checked={(state?.['selection_type']?.['business_price'] ?? 'default') === 'custom'}
                                        onChange={() => {
                                            dispatch({
                                                type: 'selection_type',
                                                payload: {
                                                    business_price: 'custom'
                                                }
                                            });
                                        }}
                                        helpText={t("Choosing this will override other settings when you create a custom price.")}
                                    />
                                </InlineStack>
                                {/* {state?.selection_type?.price === 'custom' ? renderPriceOptions() : containerState?.initialProductData?.hasVariants ? renderPriceOptionsParent() : state?.['raw_data']?.['price'] && renderShopifyData()} */}
                            </BlockStack>
                            {/* </Box> */}
                            {state?.['selection_type']?.['business_price'] == "default" && Object?.keys(effectiveBusinessPrice ?? {})?.length == 0 ? <Banner>{t("Business price rules are not available since business price settings are turned off") + ` ${t("from")} ` + from}</Banner> :
                                <div className={state?.['selection_type']?.['business_price'] == "default" ? "card_dull mt-8" : "mt-8"}>
                                    <BlockStack gap={'500'}>
                                        <Checkbox label={t("Enable business price for this product")} checked={effectiveBusinessPriceEnabled} onChange={(e) => handleChange("business_price_enabled", e)} />
                                        {effectiveBusinessPriceEnabled && <Bleed marginInline="500"><Divider /></Bleed>}
                                        {effectiveBusinessPriceEnabled && <BlockStack gap="300">
                                            <>
                                                {<Text
                                                    as="p"
                                                    variant="bodySm"
                                                    tone="caution"
                                                    fontWeight="medium">
                                                    {t("Note: The business price will be calculated based on the product’s standard price.")}
                                                </Text>}
                                                <div className='custom-priceInputs__container'>
                                                    <InlineStack gap={'200'} wrap={isMobile}>
                                                        <Select
                                                            label
                                                            labelHidden
                                                            options={priceOptions(t)}
                                                            value={effectiveBusinessPrice?.attribute}
                                                            onChange={(e) => {
                                                                handleChange("attribute", e);
                                                                handleChange("value", "")
                                                                let tempErrors = JSON.parse(JSON.stringify(businessPriceErrors));
                                                                tempErrors['attribute'] = e == "";
                                                                tempErrors['value'] = false;
                                                                setBusinessPriceErrors(JSON.parse(JSON.stringify(tempErrors)))
                                                            }}
                                                            error={selectionType?.business_price == "custom" ? businessPriceErrors['attribute'] : false}
                                                        />
                                                        {effectiveBusinessPrice?.attribute && effectiveBusinessPrice?.attribute != "" &&
                                                            <TextField
                                                                disabled={effectiveBusinessPrice?.attribute == ""}
                                                                autoComplete="off"
                                                                label
                                                                labelHidden
                                                                prefix={effectiveBusinessPrice?.attribute == "percentage_increase" || effectiveBusinessPrice?.attribute == "percentage_decrease" ? "%" : null}
                                                                value={effectiveBusinessPrice?.value}
                                                                onChange={(e) => {
                                                                    e = e.trim().replace(/[a-zA-Z-!$%^&*()@_+|~=`\\#{}\[\]:";'<>?,\/]/gi, '');
                                                                    e = e?.[0] === '.' ? '0' + e : e;
                                                                    e = (e.split(".").length - 1 > 1 ? e.slice(0, -1) : e).replace(/^(\d+.?\d{0,2})\d*$/, "$1");
                                                                    handleChange("value", e);
                                                                    let tempErrors = JSON.parse(JSON.stringify(businessPriceErrors));
                                                                    tempErrors['value'] = e == "";
                                                                    if (effectiveBusinessPrice?.attribute == "percentage_decrease") tempErrors['value'] = e == "" || +e >= 100;
                                                                    else if (effectiveBusinessPrice?.attribute == "value_decrease") {
                                                                        const { data, selection_type: selectionType, raw_data } = state;
                                                                        const { rawPriceSettings } = raw_data;
                                                                        const { currency_settings, from, shopify_price, standard_price } = rawPriceSettings;
                                                                        const { price } = data;
                                                                        const effectivePriceSettings = selectionType?.price == "default" ? standard_price : price;


                                                                        let myPrice: any = "";
                                                                        if (effectivePriceSettings?.attribute === "custom") {
                                                                            myPrice = effectivePriceSettings?.value
                                                                        }
                                                                        else if (effectivePriceSettings?.attribute == "default") {
                                                                            myPrice = shopify_price;
                                                                        }
                                                                        else {
                                                                            let formattedPrice: any = formatPrice({
                                                                                props: props,
                                                                                shopify_price: shopify_price ?? 0,
                                                                                settings_enabled: effectivePriceSettings && Object?.keys(effectivePriceSettings)?.length > 0,
                                                                                standard_price: effectivePriceSettings ?? [],
                                                                                currency_settings: currency_settings
                                                                            });
                                                                            myPrice = +(formattedPrice?.currency_settings?.calculated_value ?? 0);
                                                                        }
                                                                        tempErrors['value'] = containerState['product_type']['variantEdit'] ||
                                                                            !containerState.initialProductData['hasVariants']
                                                                            ? e === "" || +e >= myPrice
                                                                            : e === "";
                                                                    }
                                                                    setBusinessPriceErrors(JSON.parse(JSON.stringify(tempErrors)))
                                                                }}
                                                                error={selectionType?.business_price == "custom" ? businessPriceErrors['value'] : false}
                                                            />
                                                        }
                                                        {effectiveBusinessPrice?.attribute && effectiveBusinessPrice?.attribute != "" &&
                                                            <Select
                                                                id='price_Select_BusinessPrice_RoundingOff'
                                                                label={<Text as='p' tone='subdued'>{t("Rounding Off")}</Text>}
                                                                labelHidden
                                                                options={roundOffOptions}
                                                                onChange={(e) => {
                                                                    handleChange("price_roundoff_value", e);
                                                                    // onPriceSettingsChange("business_price", "price_roundoff_value", e);
                                                                    // let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                                    // if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['business_price']['price_roundoff_value'])) tempContextualSaveBarData['business_price' + '_roundOff'] = true;
                                                                    // else tempContextualSaveBarData['business_price' + '_roundOff'] = false;
                                                                    // setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                                                }}
                                                                value={effectiveBusinessPrice?.price_roundoff_value}
                                                            // helpText={`Kinldy use 'Preview price-round off' feature for exact calculation. `}
                                                            // disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.business_price}
                                                            // error={context.errors.pricing_settings.standard_price.attribute}
                                                            />
                                                        }
                                                        {(containerState['product_type']['variantEdit'] ||
                                                            !containerState.initialProductData['hasVariants']) && effectiveBusinessPrice?.attribute && effectiveBusinessPrice?.attribute != "" && effectiveBusinessPrice?.value && effectiveBusinessPrice?.value != "" && <CustomCard
                                                                background="rgba(0, 0, 0, 0.06)"
                                                                customClass="business__finalPrice">
                                                                <InlineStack
                                                                    gap={'100'}
                                                                    wrap={false}>
                                                                    <Text
                                                                        as="p"
                                                                        tone="subdued"
                                                                        fontWeight="medium">
                                                                        {t("Final Price:")}
                                                                    </Text>
                                                                    <Text
                                                                        as="p"
                                                                        fontWeight="medium">
                                                                        {`${getCurrency(props)} ${getFinalPriceWithRoundOff(state, effectiveBusinessPrice, props)}`}
                                                                    </Text>
                                                                    <Tooltip content={getBusinessPriceBreakUp()}>
                                                                        <Icon source={InfoMinor} />
                                                                    </Tooltip>
                                                                </InlineStack>
                                                            </CustomCard>}
                                                    </InlineStack>
                                                </div>
                                            </>
                                            {/* } */}
                                        </BlockStack>}
                                        {effectiveBusinessPriceEnabled && effectiveBusinessPrice?.value != "" &&
                                            <BlockStack gap={'300'}>
                                                <InlineStack gap={'200'}>
                                                    <Button
                                                        variant="plain"
                                                        disclosure
                                                        onClick={() => props.setShowBusinessPrice(!props.showBusinessPrice)}>
                                                        {t("Add discounts on orders quantity")}
                                                    </Button>
                                                </InlineStack>
                                                <Collapsible
                                                    open={effectiveShowBusinessPrice}
                                                    id="basic-collapsible"
                                                    transition={{
                                                        duration: '500ms',
                                                        timingFunction: 'ease-in-out',
                                                    }}
                                                    expandOnPrint>
                                                    <Card background="bg-surface">
                                                        <BlockStack
                                                            gap={'400'}
                                                            inlineAlign="start">
                                                            <BlockStack gap={'300'}>
                                                                <InlineStack
                                                                    align="space-between"
                                                                    gap={'300'}>
                                                                    <InlineStack
                                                                        gap={'300'}>
                                                                        <RadioButton
                                                                            label={t("Value")}
                                                                            checked={effectiveBusinessPrice?.quantity_type == "fixed"}
                                                                            id="fixed"
                                                                            name="quantityDiscounts"
                                                                            onChange={() => {
                                                                                handleChange("quantity_type", "fixed");
                                                                                let tempErrors = JSON.parse(JSON.stringify(businessPriceErrors));
                                                                                tempErrors['quantity_type'] = false;
                                                                                setBusinessPriceErrors(JSON.parse(JSON.stringify(tempErrors)))
                                                                            }}
                                                                        />
                                                                        <RadioButton
                                                                            label={t("Percentage")}
                                                                            checked={effectiveBusinessPrice?.quantity_type == "percent"}
                                                                            id="percent"
                                                                            name="quantityDiscounts"
                                                                            onChange={() => {
                                                                                handleChange("quantity_type", "percent");
                                                                                let tempErrors = JSON.parse(JSON.stringify(businessPriceErrors));
                                                                                tempErrors['quantity_type'] = false;
                                                                                setBusinessPriceErrors(JSON.parse(JSON.stringify(tempErrors)))
                                                                            }}
                                                                        />
                                                                    </InlineStack>
                                                                    {selectionType?.business_price == "custom" && businessPriceErrors?.quantity_type && <InlineError message={t("Kindly select 'Value' or 'Percentage' option to add discounts on orders quantity.")} fieldID={'quantityDiscounts'} />}
                                                                </InlineStack>
                                                                {effectiveBusinessPrice?.quantity_type && 
                                                                <BlockStack gap={"300"}>
                                                                     <Divider borderColor="border-secondary" />
                                                                    <Banner tone="info" hideIcon>
                                                                        <BlockStack
                                                                            gap={'050'}
                                                                            inlineAlign="start">
                                                                            <Text as="p">
                                                                                {/* {t("To set a new business price without giving discounts on the order quantity, first switch off the old business price to clear the data and then, switch it on again to apply the relevant settings.")} */}
                                                                                {effectiveBusinessPrice?.quantity_type == "percent" ? t("Attention: The final price displayed above is for representation only. This function syncs only the discount percentage (based on the business price) with Amazon, where the final price is then calculated and reflected.") : t("Attention: The final price displayed above is the precise product price after applying the discount, and this exact amount will be synced to Amazon.")}
                                                                            </Text>
                                                                            {/* <Button variant="plain">
                                                                Know more
                                                            </Button> */}
                                                                        </BlockStack>
                                                                    </Banner>
                                                                </BlockStack>
                                                                }
                                                                <BlockStack gap={"400"}> {quantityDiscountMarkUp}</BlockStack>
                                                            </BlockStack>
                                                            {effectiveBusinessPrice?.quantity_type && <div className='w-100'><InlineStack gap={'300'} align='space-between' blockAlign='center'>
                                                                <Button
                                                                    disabled={shouldAddRowDisabled()}
                                                                    variant="tertiary"
                                                                    icon={PlusMinor}
                                                                    onClick={() => handleRow(effectiveBusinessPrice?.quantity_type, 0, "add")}>
                                                                    {t("Add a new discount rule")}
                                                                </Button>
                                                                <Tooltip content={t("NOTE: If you want to set a new business price without offering discounts based on order quantity, first switch off the old business price to clear the data. Then, switch it on again to apply the effective settings.")} hasUnderline>
                                                                    <div>{t("Want to set a new business price without discounts?")}</div>
                                                                </Tooltip>
                                                            </InlineStack></div>}
                                                            {duplicateQuantityError && <InlineError message={t("Duplicate quantity error")} fieldID={''} />}
                                                            {hierarchyError && <InlineError message={t("Hierarchy error")} fieldID={'hierarchyError'} />}

                                                        </BlockStack>
                                                    </Card>
                                                </Collapsible>
                                            </BlockStack>}
                                    </BlockStack>
                                </div>}
                            {result && <InlineError fieldID='inlineErrorTitle' message={t("Some business price settings are missing, which are mandatory.")} />}
                        </Box>
                    </div>
                </Layout.AnnotatedSection>
            </Layout>
        </Card >
    );
};

export const priceOptions = (t: any) => {
    return [
        { label: t("Please Select"), value: "", disabled: true },
        { label: t("Percentage decrease"), value: "percentage_decrease" },
        { label: t("Value decrease"), value: "value_decrease" }
    ];
}

export const getFinalPriceWithRoundOff = (state: any, businessPrice: any, props: any) => {
    let calculatedPrice: any = +(getFinalPrice(state, businessPrice, props))?.price;
    // console.log(businessPrice, calculatedPrice, "sdvjcb");

    let result: any = "";
    switch (businessPrice?.price_roundoff_value) {
        case "higherWholeNumber":
            result = Math.ceil(+calculatedPrice);
            break;
        // case "higherEndWith9":
        //     calculatedPrice = calculatedPrice + "";
        //     if (calculatedPrice.includes(".")) result = calculatedPrice.slice(0, calculatedPrice.indexOf("."))
        //     if (calculatedPrice.length == 1) result = 9;
        //     else {
        //         let lastTwoDigits = calculatedPrice.slice(calculatedPrice.length - 2);
        //         result = calculatedPrice.slice(0, calculatedPrice.length - 2) + lastTwoDigits[0] + "9";
        //     }
        //     break;
        case "higherEndWith9":
            calculatedPrice = calculatedPrice + ""; // Convert to string if not already
            if (calculatedPrice.includes(".")) {
                calculatedPrice = calculatedPrice.slice(0, calculatedPrice.indexOf(".")); // Remove decimal part
            }

            if (calculatedPrice.length === 1) {
                // If the number has 1 digit, directly set it to 9
                result = "9";
            } else if (calculatedPrice.length === 2) {
                // If the number has 2 digits, replace the last digit with 9
                result = calculatedPrice[0] + "9";
            } else {
                // For numbers with more than 2 digits, handle normally
                let lastTwoDigits = calculatedPrice.slice(calculatedPrice.length - 2);
                result = calculatedPrice.slice(0, calculatedPrice.length - 2) + lastTwoDigits[0] + "9";
            }
            break;
        case "higherEndWith0.99":
            calculatedPrice = calculatedPrice + "";
            if (calculatedPrice.includes(".")) {
                result = calculatedPrice.slice(0, calculatedPrice.indexOf(".") + 1) + "99";
            }
            else {
                result = calculatedPrice + ".99"
            }
            break;
        case "higherEndWith0.49":
            calculatedPrice = calculatedPrice + "";
            if (calculatedPrice.includes(".")) {
                result = calculatedPrice.slice(0, calculatedPrice.indexOf(".") + 1) + "49";
            }
            else {
                result = calculatedPrice + ".49"
            }
            break;
        case "higherEndWith10":
            calculatedPrice = calculatedPrice + "";
            if (calculatedPrice.includes(".")) calculatedPrice = calculatedPrice.slice(0, calculatedPrice.indexOf("."));
            if (calculatedPrice.length == 1) result = "10";
            else if (calculatedPrice.length == 2) result = (+calculatedPrice[0] + 1) + "" + "0";
            else {
                let lastTwoDigits = calculatedPrice.slice(calculatedPrice.length - 2);
                if (lastTwoDigits[0] == '9') result = (+calculatedPrice.slice(0, calculatedPrice.length - 1) + 1) + "" + "0";
                else result = calculatedPrice.slice(0, calculatedPrice.length - 2) + (+lastTwoDigits[0] + 1) + "0";
            }
            break;
        case "lowerWholeNumber":
            result = Math.floor(+calculatedPrice);
            break;
        case "lowerEndWith9":
            calculatedPrice = calculatedPrice + "";
            if (calculatedPrice.includes(".")) calculatedPrice = calculatedPrice.slice(0, calculatedPrice.indexOf("."))
            if (calculatedPrice.length == 1) result = 9;
            else if (calculatedPrice.length == 2) {
                let first = calculatedPrice[0];
                let last = calculatedPrice[1];
                result = (+first - 1) + "" + "9";
            }
            else {
                let lastTwoDigits = calculatedPrice.slice(calculatedPrice.length - 2);
                let thirdLast = calculatedPrice.slice(0, calculatedPrice.length - 2);
                if (lastTwoDigits[0] == 0) {
                    result = (+thirdLast - 1) + "" + "99";
                }
                else result = calculatedPrice.slice(0, calculatedPrice.length - 2) + (+lastTwoDigits[0] - 1) + "" + "9";
            }
            break;
        case "lowerEndWith0.99":
            calculatedPrice = calculatedPrice == '0' ? 0 : +calculatedPrice - 1;
            calculatedPrice = calculatedPrice + "";
            if (calculatedPrice.includes(".")) {
                result = calculatedPrice.slice(0, calculatedPrice.indexOf(".") + 1) + "99";
            }
            else {
                result = calculatedPrice + ".99"
            }
            break;
        case "lowerEndWith0.49":
            calculatedPrice = calculatedPrice == '0' ? 0 : +calculatedPrice - 1;
            calculatedPrice = calculatedPrice + "";
            if (calculatedPrice.includes(".")) {
                result = calculatedPrice.slice(0, calculatedPrice.indexOf(".") + 1) + "49";
            }
            else {
                result = calculatedPrice + ".49";
            }
            break;
        case "lowerEndWith10":
            calculatedPrice = calculatedPrice + "";
            if (calculatedPrice.includes(".")) calculatedPrice = calculatedPrice.slice(0, calculatedPrice.indexOf("."))
            if (calculatedPrice.length == 1) result = "0";
            else if (calculatedPrice.length == 2) result = calculatedPrice[0] + "0";
            else {
                let lastTwoDigits1 = calculatedPrice.slice(calculatedPrice.length - 2);
                result = calculatedPrice.slice(0, calculatedPrice.length - 2) + (+lastTwoDigits1[0]) + "0";
            }
            break;
        default:
            result = calculatedPrice;
            break;
    }
    return result
}

export const getCurrency = (props: any) => {
    let currency: string = "";
    let current_shop_details = getMarketplaceId(props?.redux?.current?.target?.['warehouses']);
    if (current_shop_details) currency = current_shop_details?.['currency_symbol'];
    return currency;
}

export const getFinalPrice = (state: any, businessPrice: any, props: any) => {
    const { data, selection_type: selectionType, raw_data } = state;
    const { rawPriceSettings } = raw_data;
    const { currency_settings, from, shopify_price, standard_price } = rawPriceSettings;
    const { price } = data;
    const effectivePriceSettings = selectionType?.price == "default" ? standard_price : price;


    let myPrice: any = "";
    if (effectivePriceSettings?.attribute === "custom") {
        myPrice = effectivePriceSettings?.value
    }
    else if (effectivePriceSettings?.attribute == "default") {
        myPrice = shopify_price;
    }
    else {
        let formattedPrice: any = formatPrice({
            props: props,
            shopify_price: shopify_price ?? 0,
            settings_enabled: effectivePriceSettings && Object?.keys(effectivePriceSettings)?.length > 0,
            standard_price: effectivePriceSettings ?? [],
            currency_settings: currency_settings
        });
        myPrice = +(formattedPrice?.currency_settings?.calculated_value ?? 0);
    }
    // myPrice = +(state?.selection_type?.price == "custom" ? deafultPriceWithRules : removeCurrencySign(deafultPriceWithRules?.props?.value));
    if (myPrice && myPrice > 0) {
        if (businessPrice?.attribute && businessPrice?.value) {
            if (businessPrice?.attribute == "percentage_decrease") {
                myPrice = myPrice - (myPrice * businessPrice?.value / 100)
            }
            else if (businessPrice?.attribute == "value_decrease") {
                myPrice = myPrice - businessPrice?.value
            }
        }
    }

    let currency: string = "";
    let current_shop_details = getMarketplaceId(props?.redux?.current?.target?.['warehouses']);
    if (current_shop_details) currency = current_shop_details?.['currency_symbol'];

    // return <Text as={'span'} >{`${currency} ${Number(price.toFixed(2))}`}</Text>;
    return { currency, price: roundNumber(myPrice, 2) }
}

export const roundNumber = (num: number, places: number) => {
    // return Number(num.toFixed(places));
    return Math.round((num + Number.EPSILON) * 100) / 100
}

export function removeCurrencySign(currencyString: string) {
    // Use a regular expression to remove non-digit characters (except the decimal point)
    return currencyString.replace(/[^\d.-]/g, '');
}

export function setErrorsForDuplicates(businessPrice: any, errorsArr: any, value: any, type: any) {
    const arr = businessPrice?.[businessPrice?.quantity_type];

    // Compare each object with every other object in the array
    for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
            if (arr[i].quantity == "" || arr[i].quantity === arr[j].quantity) {
                errorsArr[i].quantity = true;
                errorsArr[j].quantity = true;
            }
            // else if ()
            if (arr[i].price === arr[j].price) {
                errorsArr[i].price = true;
                errorsArr[j].price = true;
            }
        }
    }

    return errorsArr;
}

export function validatePriceEntries(
    businessPrice: any,
    state: any,
    getFinalPrice: any,
    from: any,
    arr: any,
    errors: any,
    validation: "save" | "update",
    t: any,
    isParent: boolean = false
) {
    // console.log(businessPrice,
    //     state,
    //     getFinalPrice,
    //     from,
    //     arr, JSON.stringify(errors), validation, "dvjbndj");

    if (!arr || arr.length === 0) return [];

    // Initialize the error state with an array of messages and flags for each row
    let errorsArr = errors.map((err: any) => ({
        hasError: false,
        errors: [],
        quantity: false,
        price: false,
        touched: validation == "save" ? {
            quantity: true,
            price: true
        } : err?.touched ?? {
            quantity: true,
            price: true
        }
    }));

    // Loop through the array to validate rows (since the rows are already sorted)
    for (let i = 0; i < arr.length; i++) {
        let rowErrors: string[] = [];

        if (errorsArr?.[i]?.['touched']?.['quantity']) {
            // Check if the quantity is empty or <= 0
            if (arr[i].quantity === "" || +arr[i].quantity <= 0) {
                rowErrors.push(t("Quantity can't be empty or equal to zero."));
                errorsArr[i].quantity = true;
            }

            // Check for duplicate quantities (only set error in i+1th row)
            if (i + 1 < arr.length && arr[i].quantity === arr[i + 1].quantity) {
                errorsArr[i + 1].quantity = true;
                errorsArr[i + 1].errors.push(t("Quantity can't be the same as the previous row."));
                errorsArr[i + 1].hasError = true;
            }

            // Check if the next row (i+1) has a quantity less than the current row
            if (i + 1 < arr.length && arr[i + 1].quantity) {
                if (parseInt(arr[i + 1].quantity) < parseInt(arr[i].quantity)) {
                    errorsArr[i + 1].quantity = true;
                    errorsArr[i + 1].errors.push(t("Quantity must be greater than the previous row."));
                    errorsArr[i + 1].hasError = true;
                }
            }
        }

        if (errorsArr?.[i]?.['touched']?.['price']) {
            // Check if the current price is lower than the previous one (only set error in i+1th row)
            if (i + 1 < arr.length && parseInt(arr[i].price) === parseInt(arr[i + 1].price)) {
                errorsArr[i + 1].price = true;
                errorsArr[i + 1].errors.push(t("Discount can't be the same as the previous row."));
                errorsArr[i + 1].hasError = true;
            }

            // Additional checks for price based on `quantity_type`
            const price = arr[i].price;

            if (businessPrice?.quantity_type === "percent" || businessPrice?.quantity_type === "percentage_discount") {
                if (price === "" || +price <= 0 || +price >= 100) {
                    rowErrors.push(t("Discount percentage can't be empty or zero and should be less than 100 percent."));
                    errorsArr[i].price = true;
                }
            } else if (businessPrice?.quantity_type === "fixed" || businessPrice?.quantity_type === "fixed_discount") {
                if (from == "product_edit") {
                    const currentBusinessPrice = +getFinalPrice(state, businessPrice, null)?.price;

                    // Split the price error conditions into separate messages
                    if (price === "" || +price <= 0) {
                        rowErrors.push(t("Discount price can't be empty or equal to zero."));
                        errorsArr[i].price = true;
                    }
                    if (!isParent && +price >= currentBusinessPrice) {
                        rowErrors.push(`Discount price must be lower than ${currentBusinessPrice}(i.e business price).`);
                        errorsArr[i].price = true;
                    }
                } else {
                    if (price === "" || +price <= 0) {
                        rowErrors.push(t("Discount price can't be empty or equal to zero."));
                        errorsArr[i].price = true;
                    }
                }
            }
        }

        // If we have any errors for the row, update the `hasError` flag and store the errors
        if (rowErrors.length > 0) {
            errorsArr[i].hasError = true;
            errorsArr[i].errors = rowErrors;
        }
    }
    return JSON.parse(JSON.stringify(errorsArr));
}

export default DI(BusinessPriceN);